import React, { useState } from 'react';
import { Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../firebase';

export function ForgottenPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  
  // Validation d'email
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation d'email côté client
    if (!isValidEmail(email)) {
      setError(t('forgot_password.error.invalid_email'));
      return;
    }
    
    setStatus('loading');
    setError('');

    try {
      // Utiliser le service Firebase pour envoyer l'email de réinitialisation
      await authService.resetPasswordRequest(email);
      setStatus('success');
    } catch (err: any) {
      console.error('Password reset error:', err);
      
      // Gestion des erreurs Firebase
      if (err.code === 'auth/user-not-found') {
        // Pour des raisons de sécurité (prévention d'énumération d'emails),
        // nous simulons un succès même si l'utilisateur n'existe pas
        setStatus('success');
      } else if (err.code === 'auth/invalid-email') {
        setError(t('forgot_password.error.invalid_email'));
        setStatus('error');
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('forgot_password.error.too_many_requests'));
        setStatus('error');
      } else {
        setError(t('forgot_password.error.generic'));
        setStatus('error');
      }
    }
  };

  return (
    <div className="max-w-md w-full space-y-8 bg-white bg-opacity-90 dark:bg-black dark:bg-opacity-90 border border-gray-200 dark:border-gray-700 p-8 rounded-2xl shadow-2xl backdrop-blur-lg">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('forgot_password.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          {t('forgot_password.subtitle')}
        </p>
      </div>

      {status === 'success' ? (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-900 text-green-600 dark:text-green-400 px-4 py-3 rounded-lg">
          {t('forgot_password.success')}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('forgot_password.email.label')}
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                          bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500
                          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('forgot_password.email.placeholder')}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {status === 'loading' ? t('forgot_password.sending') : t('forgot_password.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
